import React, { createContext, useContext, useState, useEffect } from 'react'
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query'
import { apiGet, apiPatch } from '~/util/api'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { Toaster } from '~/lib/ui/toast'

const pathPrefix = '/coach/dashboard_candidates/'

const CandidateContext = createContext()

export const useCandidate = () => useContext(CandidateContext)

export const queryClient = new QueryClient()

const CandidateProviderInner = ({ coach_id, children }) => {
  const location = useLocation()
  const candidateId = location.pathname.split('/').pop()
  const [detailType, detailItemId] = location.hash?.substr(1)?.split('/') || []
  const urlParams = new URLSearchParams(window.location.search)
  const activeTabParam = urlParams?.get('active_tab')
  const defaultActiveTab = activeTabParam || 'coach_services'

  const queryPath = [pathPrefix, candidateId, '.json'].join('')
  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { error, data: candidateData } = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn,
  })

  if (error) {
    console.error('error retrieving candidate', error)
  }
  const { candidate } = candidateData || {}

  const [candidateStatus, setCandidateStatus] = useState(
    candidate?.candidateStatus,
  )

  const [showCandidateStatusDialog, setShowCandidateStatusDialog] =
    useState(false)

  const toggleShowCandidateStatusDialog = () =>
    setShowCandidateStatusDialog(!showCandidateStatusDialog)

  useEffect(() => {
    if (candidate?.candidateStatus) {
      setCandidateStatus(candidate.candidateStatus)
    }
  }, [candidate])

  const [candidateState, setCandidateState] = useState(candidate)

  // Update candidateState when candidateData changes
  useEffect(() => {
    if (candidate) {
      setCandidateState(candidate)
    }
  }, [candidate])

  // Update setNewCandidateStatus to update candidateState as well
  const setNewCandidateStatus = async (v) => {
    const res = await apiPatch(
      `/coach/candidates/${candidate.id}/assign_candidate_state`,
      {
        candidate: { candidate_profile_attributes: { candidate_state: v } },
        _method: 'patch',
      },
    )

    const data = res.data
    if (res.status === 200) {
      setCandidateStatus(data.candidateStatus)
      setCandidateState(data) // Update the candidate object
      setShowCandidateStatusDialog(false)
      return res
    } else {
      throw new Error('res not ok')
    }
  }

  const value = {
    candidate: candidateState,
    setCandidate: setCandidateState, // <-- expose setter
    detailType,
    detailItemId,
    defaultActiveTab,
    candidateStatus,
    setCandidateStatus,
    showCandidateStatusDialog,
    setShowCandidateStatusDialog,
    toggleShowCandidateStatusDialog,
    setNewCandidateStatus,
    coachId: coach_id,
  }
  return (
    <CandidateContext.Provider value={value}>
      {children}
      <Toaster />
    </CandidateContext.Provider>
  )
}

export const CandidateProvider = ({ children, ...props }) => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <CandidateProviderInner {...props}>{children}</CandidateProviderInner>
      </QueryClientProvider>
    </BrowserRouter>
  )
}
export default CandidateProvider
